/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo } from 'react'
import type { Entry } from 'contentful'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import Card, { type CardProps } from '@/components/ui/card'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'

import { cx, slugify } from '@/utils/strings'

import { Experience } from '@ninetailed/experience.js-react'
import { mapNtExperiences } from '@/utils/ninetailed'

import PricingCard, { type PricingCardProps } from '@/components/ui/pricing-card'
import Image from '@/components/ui/image'
import CodeTabs from '@/components/ui/code/code-tabs'
import {
  isTypeComponentCard,
  type TypeBlogPageSkeleton,
  type TypeComponentCardSkeleton,
  type TypeComponentPricingSkeleton,
  type TypeLandingPageSkeleton,
  type TypeResourceImageSkeleton,
  type TypeResourcePageSkeleton,
  type TypeResourceVideoPageSkeleton,
  type TypeSectionSetOfCardsSkeleton,
} from '@/types/ctf'
import ShieldLoader from '@/components/ui/shield-loader'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import GradientBackgroundWrapper from '@/components/ui/gradient-background-wrapper'

type CardEntry = Entry<TypeComponentCardSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type BlogPageEntry = Entry<TypeBlogPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ResourcePageEntry = Entry<TypeResourcePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCardsEntry = Entry<TypeSectionSetOfCardsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

export type SetOfCardsProps = SetOfCardsEntry['fields'] & {
  id?: string
  isHero?: boolean
  pageOrderNo?: number
  isPrintView?: boolean
  filter?: {
    search?: string
    tags?: string[]
  }
  imageType?: string
  showHeroImageOnMobile?: boolean
}

// eslint-disable-next-line react/function-component-definition
export const SetOfCardsSectionComponent = ({
  isHero,
  pageOrderNo,
  isPrintView,
  actionsPosition,
  internalName,
  headline,
  anchorLinkTitle,
  body,
  cards,
  actions,
  backgroundColor,
  cardIconSize,
  cardIconLocation,
  cardsPerColumn,
  stackImageOverText,
  unconstrained,
  excludeFromPdf,
  filter,
  imageType,
  transparentCards,
  heroLayout = 'Horizontal',
  showHeroImageOnMobile,
  eyebrowText,
}: SetOfCardsProps) => {
  // if cards per column is not explicitly set, as long as there are 6 or fewer cards, show all cards in a single row. Otherwise default to 3 per row.
  const columnCount = cardsPerColumn || (Array.isArray(cards) && cards?.length <= 6 ? cards?.length : 3) || 1

  const HeadingWrap = useCallback(
    ({ children }: { children: string }) => {
      if (pageOrderNo === 1) {
        return anchorLinkTitle ? (
          <HeadingWithAnchor
            type="h2"
            title={children}
            id={anchorLinkTitle}
            className="h3 relative inline-block header-gradient"
          />
        ) : (
          <h2 className={cx('text-center h3 header-gradient text-balance', isHero && 'h1')}>{children}</h2>
        )
      }
      return anchorLinkTitle ? (
        <HeadingWithAnchor
          type="h3"
          title={children}
          id={anchorLinkTitle}
          className="h3 header-gradient relative inline-block"
        />
      ) : (
        <h3 className={cx('h3 text-center text-balance header-gradient', isHero && 'h1')}>{children}</h3>
      )
    },
    [anchorLinkTitle, isHero, pageOrderNo]
  )

  const renderActions = useCallback(
    () => (
      <div className="flex flex-wrap justify-center items-center gap-4">
        {actions?.map((action) => {
          if (!action) return null
          const type = action.sys.contentType.sys.id
          const fields = {
            type,
            ...action.fields,
          } as DynamicActionProps
          return <DynamicAction key={action?.sys.id} id={action?.sys.id} {...fields} data-testid="card-cta" />
        })}
      </div>
    ),
    [actions]
  )

  // Grid wrapper component that only renders grid container when needed
  const GridWrapper = useCallback(
    ({ children }: { children: React.ReactNode }) => {
      if (columnCount >= 2) {
        return (
          <div
            className={cx(
              'grid gap-x-6 gap-y-6 xl:gap-y-12',
              columnCount === 2 && `lg:grid-cols-2 xl:gap-x-8`,
              columnCount === 3 && `lg:grid-cols-3 xl:gap-x-8`,
              columnCount === 4 && `lg:grid-cols-4 xl:gap-x-8`,
              columnCount === 5 && `lg:grid-cols-5 xl:gap-x-6`,
              columnCount === 6 && `lg:grid-cols-6 xl:gap-x-6`,
              actions && `mb-10`
            )}
          >
            {children}
          </div>
        )
      }
      return <>{children}</>
    },
    [columnCount, actions]
  )

  const shouldFilter = typeof filter !== 'undefined'

  // Memoize the filtered cards computation to prevent recalculation on every render
  const filteredCards = useMemo(() => {
    if (!shouldFilter) return []

    return (
      cards?.map((card) => {
        if (!card) return true

        if (isTypeComponentCard(card)) {
          // Filter by search term
          if (filter.search) {
            const searchTerms = filter.search.toLowerCase().split(' ').filter(Boolean)
            const subtext =
              typeof card.fields.subtext !== 'string'
                ? card.fields.subtext
                  ? documentToPlainTextString(card.fields.subtext)
                  : ''
                : card.fields.subtext || ''
            if (
              searchTerms.every(
                (term) => !card.fields.headline?.toLowerCase().includes(term) && !subtext.toLowerCase().includes(term)
              )
            ) {
              return true
            }
          }
          // Filter by active filter tags
          if (filter.tags && filter.tags.length > 0) {
            const tags = card.fields.filterTags
            // Hide card if it doesn't have any of the active filters
            if (!tags || !tags.some((tag) => filter.tags?.includes(tag))) {
              return true
            }
          }
        }

        return false
      }) || []
    )
  }, [cards, filter, shouldFilter])

  const areAllCardsFiltered = shouldFilter && filteredCards.every((card) => card)
  if (areAllCardsFiltered) return null

  // Memoize card props to prevent object recreation on every render
  const cardIconLocationForIndex = useMemo(
    () => (idx: number) =>
      columnCount === 1 && idx % 2 === 1
        ? ({ Right: 'Left', Left: 'Right' }[cardIconLocation] as typeof cardIconLocation)
        : cardIconLocation,
    [columnCount, cardIconLocation]
  )

  const baseCardProps = useMemo(
    () => ({
      cardIconSize,
      columnCount,
      isHero: isHero || false,
      stackImageOverText,
      unconstrained,
      imageType,
      transparentBg: transparentCards,
      heroLayout,
      showHeroImageOnMobile,
      sectionHasHeadline: !!headline,
      backgroundStyle: backgroundColor,
    }),
    [
      cardIconSize,
      columnCount,
      isHero,
      stackImageOverText,
      unconstrained,
      imageType,
      transparentCards,
      heroLayout,
      showHeroImageOnMobile,
      headline,
      backgroundColor,
    ]
  )

  if (areAllCardsFiltered) return null

  // reset backgroundStyle if its one of the legacy options no longer supported
  if (
    backgroundColor === 'White' ||
    backgroundColor === 'Transparent' ||
    backgroundColor === 'Primary Blue' ||
    backgroundColor === 'Indigo Blue' ||
    backgroundColor === 'Light Gray'
  ) {
    backgroundColor = undefined
  }

  return isPrintView ? (
    !excludeFromPdf && (
      <div className="border-b-[0.25pt] py-3">
        {headline && <h2 className={cx(!body && 'py-3')}>{headline}</h2>}
        {body && (
          <div className="[&_p]:text-[9pt]! [&_a]:text-[9pt]!">
            <RichText body={body} />
          </div>
        )}
        <div
          className={cx(
            (cards?.length ?? 0) > 1 && 'grid grid-cols-2 gap-x-6',
            (cards?.length ?? 0) === 6 && 'grid grid-cols-3 gap-x-6',
            (cards?.length ?? 0) === 3 && 'grid grid-cols-3 gap-x-4'
          )}
        >
          {cards?.map((card: any) => (
            <div key={card.sys.id} className={cx((cards?.length ?? 0) === 1 && 'mt-3')}>
              {card?.fields?.headline && (
                <h3 className={cx('text-pretty', (cards?.length ?? 0) > 1 && 'text-[10pt]!')}>
                  {card.fields.headline}
                </h3>
              )}
              {card?.fields?.subtext && <RichText body={card.fields.subtext} />}
            </div>
          ))}
        </div>
      </div>
    )
  ) : (
    <GradientBackgroundWrapper
      id={slugify(internalName)}
      backgroundStyle={backgroundColor}
      unconstrained={unconstrained}
      containerClassName={cx(
        isHero && 'cards-hero',
        !backgroundColor && 'max-w-8xl mx-auto relative overflow-hidden px-5 lg:px-9',
        !columnCount || (columnCount === 1 && 'grid gap-y-10')
      )}
      innerClassName={cx(backgroundColor && 'px-5 lg:px-9 py-6 lg:py-16')}
    >
      {(!!headline?.length || !!body?.fields) && (
        <div
          className={cx('text-center max-w-6xl mx-auto px-5', columnCount >= 2 && 'pb-6 lg:pb-12', isHero && 'py-20')}
        >
          {eyebrowText && <p className="text-eyebrow uppercase">{eyebrowText}</p>}
          {headline && <HeadingWrap>{headline}</HeadingWrap>}
          {body && (
            <div className={cx(!actions && '[&_p]:mb-0')}>
              <RichText body={body} />
            </div>
          )}
          {actions && actionsPosition !== 'Below cards' && renderActions()}
        </div>
      )}

      {cards && (
        <GridWrapper>
          {Array.isArray(cards) &&
            cards.map((card, idx) => {
              if (!card) return null
              let cardProps: Partial<CardProps> = {}

              switch (card.sys.contentType.sys.id) {
                case 'componentCard':
                  cardProps = card.fields as CardProps

                  // Hide card if it's filtered out
                  if (filteredCards[idx]) {
                    cardProps.className = 'hidden'
                  } else {
                    cardProps.className = ''
                  }

                  const actions = (card.fields as CardEntry['fields']).actions as
                    | CardEntry['fields']['actions']
                    | DynamicActionProps[]
                  if (actions) {
                    cardProps.actions = actions
                      .map((action) => {
                        if (!action) return null
                        if ((action as DynamicActionProps).type) return action as DynamicActionProps
                        const type = action.sys?.contentType.sys.id
                        const fields = {
                          type,
                          id: action.sys.id,
                          ...action.fields,
                        } as DynamicActionProps
                        return fields
                      })
                      .filter((action) => !!action) as DynamicActionProps[]
                  }
                  break
                case 'blogPage':
                  const blogCard = card as BlogPageEntry
                  cardProps = {
                    tagline: 'Blog',
                    headline: blogCard.fields.title,
                    subtext: blogCard.fields.description,
                    image: blogCard.fields.heroImage,
                    imageStyle: blogCard.fields.imageStyle,
                    tags: blogCard.fields.tags,
                    actions: [
                      {
                        id: blogCard.sys.id,
                        to: `/blog/${blogCard.fields.slug}`,
                      },
                    ],
                  }
                  break
                case 'resourcePage':
                  const resourceCard = card as ResourcePageEntry
                  cardProps = {
                    tagline: 'Resource',
                    headline: resourceCard.fields.title,
                    subtext: resourceCard.fields.description,
                    image: resourceCard.fields.image || resourceCard.fields.seo?.fields.ogImage,
                    imageStyle: resourceCard.fields.imageStyle,
                    tags: resourceCard.fields.resourceTags?.map((tag) => tag.fields.name),
                    actions: [
                      {
                        type: 'ctaLink',
                        id: resourceCard.sys.id,
                        to: `/resources/${resourceCard.fields.slug}`,
                      },
                    ],
                  }
                  break
                case 'resourceVideoPage':
                  const resourceVideoCard = card as Entry<TypeResourceVideoPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
                  cardProps = {
                    tagline: 'Video',
                    headline: resourceVideoCard.fields.title,
                    subtext: resourceVideoCard.fields.image?.fields.description,
                    image: resourceVideoCard.fields.image || resourceVideoCard.fields.seo?.fields.ogImage,
                    actions: [
                      {
                        type: 'ctaLink',
                        id: resourceVideoCard.sys.id,
                        to: `/resources/videos/${resourceVideoCard.fields.slug}`,
                      },
                    ],
                  }
                  break
                case 'landingPage':
                  const pageCard = card as Entry<TypeLandingPageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
                  cardProps = {
                    headline: pageCard?.fields.seo?.fields.title,
                    subtext: pageCard?.fields.seo?.fields.description,
                    image: pageCard?.fields.seo?.fields.ogImage,
                    actions: [
                      {
                        type: 'ctaLink',
                        id: pageCard.sys.id,
                        to: `/${pageCard.fields.slug}`,
                      },
                    ],
                  }
                  break
                case 'componentCodeTabs':
                  return <CodeTabs key={card.sys.id} code={(card as any).fields.code} />
                case 'componentPricing':
                  const {
                    fields: { price, features, actions: pricingCardActions, ...pricingCard },
                  } = card as Entry<TypeComponentPricingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
                  const pricingCardProps: PricingCardProps = {
                    ...pricingCard,
                    price,
                    features: features as string[],
                    actions: pricingCardActions,
                    hasShadow: true,
                  }
                  return <PricingCard key={card.sys.id} {...pricingCardProps} />
                case 'resourceImage':
                  return (
                    <Image
                      key={card.sys.id}
                      {...(card.fields as Entry<TypeResourceImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields'])}
                    />
                  )
                default:
                  break
              }
              return (
                <Card
                  key={card.sys.id}
                  {...cardProps}
                  {...baseCardProps}
                  cardIconLocation={cardIconLocationForIndex(idx)}
                />
              )
            })}
        </GridWrapper>
      )}

      {actions && actionsPosition === 'Below cards' && renderActions()}
    </GradientBackgroundWrapper>
  )
}

export default function SetOfCardsSection(props: SetOfCardsProps): React.JSX.Element {
  const { nt_experiences, id = '', isHero, pageOrderNo, filter } = props

  const ShieldBeatLoader = useMemo(() => {
    return () => <ShieldLoader containerHeight="500" />
  }, [])
  return (
    <Experience
      {...props}
      id={id}
      experiences={mapNtExperiences(nt_experiences)}
      component={SetOfCardsSectionComponent}
      passthroughProps={{ isHero, pageOrderNo, filter }}
      loadingComponent={ShieldBeatLoader}
    />
  )
}
